import { SvgIcon } from "@progress/kendo-react-common";
import { AppBar, AppBarSection, CardTitle } from "@progress/kendo-react-layout";
import { bellIcon, gearIcon, questionCircleIcon, securityLockIcon } from "@progress/kendo-svg-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../app/hooks";
import logoUrl from "../assets/logo-200.png";
import Notifications from "../components/notify/notifications";
import { AuthSessionGuard } from "../features/auth/authSessionGuard";
import { SignalRProvider } from "../features/signalr/signalRProvider";
import { AppStorage } from "../features/storage/appStorage";
import { logout } from "../slices/userSlice";
import { isAdministrator } from "../utils/userRoleUtils";

const menuItems: Array<{ path: string; label: string }> = [
    { path: "/app/workspace/planning", label: "Planning" },
    { path: "/app/workspace/simulator", label: "Simulator" },
    { path: "/app/workspace/scenarios", label: "Scenarios" }
];

const LoggedInLayout: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const layoutContainerRef = useRef<HTMLDivElement>(null);
    const menuContainerRef = useRef<HTMLDivElement>(null);
    const userMenuButtonRef = useRef<HTMLButtonElement>(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

    const isAuthenticated = AppStorage.isAuthenticated();
    const userEmail = AppStorage.getUserEmail();
    const userIsAdministrator = isAdministrator();

    const userInitial = useMemo(() => {
        if (!userEmail) {
            return "U";
        }

        return userEmail.trim().charAt(0).toUpperCase() || "U";
    }, [userEmail]);

    const handleLogout = () => {
        dispatch(logout());
        setIsUserMenuOpen(false);
        navigate("/");
    };

    useEffect(() => {
        if (!isUserMenuOpen) {
            return;
        }

        const updateMenuPosition = () => {
            const layoutContainer = layoutContainerRef.current;
            const userMenuButton = userMenuButtonRef.current;
            const menuContainer = menuContainerRef.current;

            if (!layoutContainer || !userMenuButton || !menuContainer) {
                return;
            }

            const layoutBounds = layoutContainer.getBoundingClientRect();
            const buttonBounds = userMenuButton.getBoundingClientRect();
            const menuBounds = menuContainer.getBoundingClientRect();

            setMenuPosition({
                top: buttonBounds.bottom - layoutBounds.top + 30,
                left: layoutBounds.left + menuBounds.width - 23
            });
        };

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target;

            if (!(target instanceof Node)) {
                return;
            }

            if (menuContainerRef.current?.contains(target)) {
                return;
            }

            setIsUserMenuOpen(false);
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsUserMenuOpen(false);
            }
        };

        updateMenuPosition();
        window.addEventListener("resize", updateMenuPosition);
        window.addEventListener("scroll", updateMenuPosition, true);
        window.addEventListener("mousedown", handlePointerDown);
        window.addEventListener("keydown", handleEscape);

        return () => {
            window.removeEventListener("resize", updateMenuPosition);
            window.removeEventListener("scroll", updateMenuPosition, true);
            window.removeEventListener("mousedown", handlePointerDown);
            window.removeEventListener("keydown", handleEscape);
        };
    }, [isUserMenuOpen]);

    return (
        <div className="d-flex flex-column vh-100 overflow-hidden">
            <Notifications />
            <AppBar className="flex-shrink-0 border border-secondary-subtle shadow-sm px-4 py-4 bg-white align-items-center justify-content-between">
                <AppBarSection className="flex-grow-1 align-items-center gap-5">
                    <CardTitle className="mb-0">
                        <span className="d-inline-flex align-items-center gap-2">
                            <img
                                src={logoUrl}
                                alt="Sovra"
                                style={{
                                    height: "1.5rem",
                                    width: "auto",
                                    display: "block",
                                    objectFit: "contain"
                                }}
                            />
                        </span>
                    </CardTitle>

                    <ul className="nav nav-pills gap-2 w-100 mb-0">
                        {menuItems.map(item => (
                            <li key={item.path} className="nav-item">
                                <NavLink
                                    to={item.path}
                                    className={({ isActive }) => `nav-link rounded-2 px-3 py-2 ${isActive
                                        ? "active !k-bg-primary border-primary text-white"
                                        : "text-body"
                                    }`}
                                >
                                    {item.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </AppBarSection>

                {isAuthenticated && (
                    <AppBarSection style={{ marginLeft: "auto" }}>
                        <div className="d-flex align-items-center gap-2" ref={layoutContainerRef}>
                            <button
                                type="button"
                                className="btn btn-link p-0 text-body text-decoration-none"
                                aria-label="Notifications"
                                title="Notifications"
                            >
                                <SvgIcon icon={bellIcon} size="large" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-link p-0 text-body text-decoration-none"
                                aria-label="Help"
                                title="Help"
                            >
                                <SvgIcon icon={questionCircleIcon} size="large" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-link p-0 text-body text-decoration-none"
                                aria-label="Settings"
                                title="Settings"
                                onClick={() => navigate("/app/settings")}
                            >
                                <SvgIcon icon={gearIcon} size="large" />
                            </button>
                            {userIsAdministrator && (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 text-body text-decoration-none"
                                    aria-label="Admin"
                                    title="Admin"
                                    onClick={() => navigate("/app/admin")}
                                >
                                    <SvgIcon icon={securityLockIcon} size="large" />
                                </button>
                            )}
                            <button
                                className="btn btn-link p-0 rounded-circle text-decoration-none"
                                ref={userMenuButtonRef}
                                type="button"
                                onClick={() => setIsUserMenuOpen(current => !current)}
                                title={userEmail ?? "User menu"}
                                aria-expanded={isUserMenuOpen}
                                aria-label="Toggle user menu"
                            >
                                <span
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "2rem",
                                        height: "2rem",
                                        borderRadius: "9999px",
                                        fontWeight: 600,
                                        backgroundColor: "#e4e7eb"
                                    }}
                                >
                                    {userInitial}
                                </span>
                            </button>
                        </div>
                    </AppBarSection>
                )}
            </AppBar>
            {isAuthenticated && isUserMenuOpen && (
                <div
                    ref={menuContainerRef}
                    className="dropdown-menu show p-2"
                    style={{
                        position: "absolute",
                        top: menuPosition.top,
                        left: menuPosition.left,
                        transform: "translateX(-100%)",
                        zIndex: 1050
                    }}
                >
                    <button type="button" className="dropdown-item" onClick={handleLogout}>
                        Log out
                    </button>
                </div>
            )}
            <SignalRProvider autoConnect={isAuthenticated}>
                <div className="flex-grow-1 min-h-0 overflow-hidden d-flex flex-column">
                    <Outlet />
                </div>
            </SignalRProvider>
            <AuthSessionGuard />
        </div>
    );
};

export default LoggedInLayout;
